import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../../../services/api";
import ReportsFilters from "../../../components/reports/ReportsFilters";
import TopItemsTable from "../../../components/reports/TopItemsTable";
import {
  buildDefaultFilters,
  buildReportParams,
  currency,
  ReportFilters,
} from "./filterUtils";

interface ItemsReportResponse {
  topItems: Array<{
    name: string;
    orders: number;
    revenue: number;
  }>;
  itemsByChannel: Array<{
    item: string;
    online: number;
    dineIn: number;
    takeaway: number;
  }>;
}

const initialData: ItemsReportResponse = {
  topItems: [],
  itemsByChannel: [],
};

const truncateLabel = (value: string, maxLength = 28) => {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1)}…`;
};
const compactCurrency = (value: number) => `$${Number(value).toFixed(0)}`;

const ItemsReport: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilters>(buildDefaultFilters());
  const [data, setData] = useState<ItemsReportResponse>(initialData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const res = await api.get<ItemsReportResponse>("/reports/items", {
          params: buildReportParams(filters),
        });
        setData(res.data);
      } catch (error) {
        console.error("Failed to fetch items report", error);
        setData(initialData);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [filters]);

  const revenueChartData = useMemo(
    () => {
      return data.topItems
        .filter((item) => item.revenue > 0)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 12)
        .map((item) => ({
          name: item.name,
          shortName: truncateLabel(item.name),
          revenue: item.revenue,
        }));
    },
    [data.topItems],
  );
  const hasRevenueChartData = revenueChartData.length > 0;
  const mobileRevenueChartData = useMemo(
    () =>
      revenueChartData.slice(0, 6).map((item) => ({
        ...item,
        mobileName: truncateLabel(item.name, 16),
      })),
    [revenueChartData],
  );

  return (
    <div className="mx-auto h-screen max-w-7xl overflow-y-auto no-scrollbar p-4 pt-0 lg:p-8">
      <div className="sticky top-0 z-10 bg-white/90 py-6 backdrop-blur-sm">
        <h1 className="text-2xl font-bold text-gray-800">Items Report</h1>
      </div>

      <ReportsFilters value={filters} onChange={setFilters} />
      {loading ? (
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : (
        <>
          <div className="mb-6">
            <h2 className="mb-3 text-lg font-semibold text-gray-800">Items (Full Menu)</h2>
            <TopItemsTable items={data.topItems} />
          </div>

          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-lg font-semibold text-gray-800">Item Sales by Channel</h2>
            <div className="overflow-x-auto">
              <div className="max-h-[430px] overflow-y-auto">
                <table className="min-w-full border-collapse">
                  <thead className="bg-gray-50 text-xs text-gray-700 sm:text-sm">
                    <tr className="border-b border-gray-200">
                      <th className="p-3 text-left font-semibold">Item</th>
                      <th className="p-3 text-center font-semibold">Online</th>
                      <th className="p-3 text-center font-semibold">Dine-In</th>
                      <th className="p-3 text-center font-semibold">Takeaway</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.itemsByChannel.map((row) => (
                      <tr key={row.item} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="p-3 text-sm text-gray-700">{row.item}</td>
                        <td className="p-3 text-center text-sm text-gray-700">{row.online}</td>
                        <td className="p-3 text-center text-sm text-gray-700">{row.dineIn}</td>
                        <td className="p-3 text-center text-sm text-gray-700">{row.takeaway}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="mb-1 text-lg font-semibold text-gray-800">Item Revenue Chart</h2>
            <p className="mb-3 text-xs text-gray-500 sm:hidden">Top 6 items by revenue</p>
            <p className="mb-3 hidden text-xs text-gray-500 sm:block">Top 12 items by revenue</p>
            {hasRevenueChartData ? (
              <div className="w-full">
                <div className="h-[320px] w-full sm:hidden">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={mobileRevenueChartData}
                      layout="vertical"
                      margin={{ top: 6, right: 8, left: 0, bottom: 6 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => compactCurrency(Number(value))}
                      />
                      <YAxis
                        type="category"
                        dataKey="mobileName"
                        width={108}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        formatter={(value) => currency(Number(value))}
                        labelFormatter={(_, payload) =>
                          payload?.[0]?.payload?.name ?? ""
                        }
                      />
                      <Bar dataKey="revenue" barSize={16} fill="#2563eb" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="hidden h-[420px] w-full sm:block">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={revenueChartData}
                      layout="vertical"
                      margin={{ top: 8, right: 20, left: 20, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => currency(Number(value))}
                      />
                      <YAxis
                        type="category"
                        dataKey="shortName"
                        width={220}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => currency(Number(value))}
                        labelFormatter={(_, payload) =>
                          payload?.[0]?.payload?.name ?? ""
                        }
                      />
                      <Bar dataKey="revenue" fill="#2563eb" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="flex h-[320px] items-center justify-center text-sm text-gray-500">
                No data available for selected filters.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ItemsReport;
